/**
 * Venta por paquete (VTA-01): cuántas unidades de Random forman una unidad de
 * la tienda. Random no tiene el dato —lo cuenta todo en su unidad mínima—, así
 * que se carga acá; la planilla del cliente ya dejó cargados los que conocía.
 *
 * Al guardar, el servidor recalcula precio y stock y devuelve el producto:
 * la vista previa de abajo es solo orientativa (redondea distinto que Random).
 */
import { useState } from 'react';
import type { FormEvent } from 'react';

import type { ProductoAdminDetalle } from '@elcarpintero/compartido';

import { actualizarPaqueteProducto } from '../../servicios/administracion.servicio';
import { numero, pesos } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { textoPaquete } from '../../utilidades/paquete';
import { Icono } from '../comunes/Icono';

/** Mismo tope que el backend (`PAQUETE_MAXIMO`). */
const MAXIMO = 10_000;

export interface FormularioPaqueteProps {
  producto: ProductoAdminDetalle;
  onGuardado: (producto: ProductoAdminDetalle) => void;
}

export function FormularioPaquete({ producto, onGuardado }: FormularioPaqueteProps) {
  const [escrito, setEscrito] = useState(String(producto.unidadesPorPaquete));
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  const unidades = Number(escrito);
  const valido = Number.isInteger(unidades) && unidades >= 1 && unidades <= MAXIMO;
  const cambio = valido && unidades !== producto.unidadesPorPaquete;
  const precioPieza = producto.precio / producto.unidadesPorPaquete;

  async function guardar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    if (!cambio) return;
    setGuardando(true);
    setFalla(null);
    try {
      onGuardado(await actualizarPaqueteProducto(producto.id, { unidadesPorPaquete: unidades }));
      setGuardado(true);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form
      onSubmit={(evento) => {
        void guardar(evento);
      }}
    >
      {falla !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{falla}</span>
        </p>
      ) : null}

      <div className="campo">
        <label htmlFor="prod-paquete">Unidades de Random por unidad de la tienda</label>
        <input
          id="prod-paquete"
          className="entrada"
          type="number"
          inputMode="numeric"
          min={1}
          max={MAXIMO}
          step={1}
          value={escrito}
          aria-describedby="ayuda-paquete"
          aria-invalid={!valido}
          onChange={(evento) => {
            setEscrito(evento.target.value);
            setGuardado(false);
          }}
        />
        <p id="ayuda-paquete" className="campo-ayuda">
          {!valido
            ? `Tiene que ser un número entero entre 1 y ${numero(MAXIMO)}.`
            : unidades === 1
              ? 'Se vende suelto: una unidad en la tienda es una unidad en Random.'
              : `${textoPaquete({ ...producto, unidadesPorPaquete: unidades }) ?? ''}: la tienda lo muestra a cerca de ${pesos(
                  precioPieza * unidades,
                )} y cada uno que se compra le descuenta ${numero(unidades)} a Random.`}
        </p>
      </div>

      <div className="admin-acciones">
        <button type="submit" className="btn btn-primario" disabled={!cambio || guardando}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        {guardado ? (
          <span className="etiqueta-ok" role="status">
            <Icono nombre="check" />
            Guardado
          </span>
        ) : null}
      </div>
    </form>
  );
}
